// ./src/components/features/Events/EventsList/EventsList.tsx
//
// The All Events list, one presentation per breakpoint (switched live on resize):
//
// - Desktop (>= md): a TABLE — type · event · when · how guests join (+ format) · status · Remove.
// - Tablet (sm–md): `EventCard`s with the Remove button visible.
// - Phones (< sm): the same card inside a `SwipeableCard` — Remove and Copy are swipe actions, never buttons on the card.
// ROADMAP #116 — Copy (a new draft from this one) on the account's own events: `canCopyEvent`'s record half (`duplicate`).
//
// ⚠⚠ WHY (owner, 2026-10-07): "/events/all … shows card in mobile view along with remove action on it. While the
// house rule is to use swipeable actions." The page rendered one card at every width, with the trash button on it.
//
// Opening an event is NAVIGATION, not editing: the row/card opens its dashboard on tap. Remove is the only row
// action, offered only where the server granted it (`eventRemovable` — an unpublished event).
// The skeleton is drawn per breakpoint in the SAME shell as the real rows (table rows in the table, card shapes in
// the card stack) and belongs to the first read only (`isLoaded`, a REQUIRED prop — this list takes its rows as props).
// The empty / no-match states stay on the page: they depend on its tabs and search.

import React, { useCallback, useMemo } from 'react';
import { ActionIcon, Badge, Box, Card, Group, Paper, Skeleton, Stack, Table, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useAppEnvironment } from '@/contexts';
import { useOptionalEventSetupCatalogContext } from '@/contexts/EventSetupCatalogContext';
import { AppIcon, CardSkeletonLines, SwipeableCard, SwipeAction, useSwipeableList } from '@/components/generic';
import { Event } from '@/utils/api';
import { recordHasRight } from '@/utils';

import { EventCard } from '../EventCard/EventCard';
import { EventModeBadge } from '../EventModeBadge';
import { EVENT_SCHEDULE_BADGES, eventFormatLabel, eventRemovable, eventStatusMeta, eventWhenText } from '../eventListHelpers';

export interface EventsListProps {
  /** The events to show (already split by tab and searched by the page). */
  events: Event[];
  /** Whether the FIRST read has come back — until then the skeleton shows. Required: the rows come only as props. */
  isLoaded: boolean;
  /** Open the event's dashboard. */
  onOpen: (event: Event) => void;
  /** Ask to remove an event — offered only on one the server lets go (`eventRemovable`). */
  onRemove?: (event: Event) => void;
  /** #116 — copy an event — offered only on the account's own (`duplicate` in its rights). */
  onCopy?: (event: Event) => void;
  /** Whether that event's removal is in flight. */
  isRemoving: (id: string) => boolean;
}

const SKELETON_ROWS = 5;

// Column widths shared by the real rows and the skeleton rows, so the swap does not move anything.
const COL = {
  type: 64,
  when: 230,
  // How guests join (2026-10-07), with the format under it — one column, so the table still fits beside the nav.
  join: 170,
  status: 130,
  actions: 84,
};

// Below this the table scrolls sideways rather than squeezing the event's name to nothing.
const TABLE_MIN_WIDTH = 900;

// ============================================
// Desktop: table row
// ============================================

interface RowProps {
  event: Event;
  onOpen: (event: Event) => void;
  onRemove?: (event: Event) => void;
  onCopy?: (event: Event) => void;
  removing: boolean;
}

const EventTableRow: React.FC<RowProps> = ({ event, onOpen, onRemove, onCopy, removing }) => {
  const { t } = useTranslation(['events', 'common']);
  const type = useOptionalEventSetupCatalogContext()?.findType(event.eventCategoryId) ?? null;
  const status = eventStatusMeta(event.eventStatus);
  const scheduleBadge = event.scheduleMode ? EVENT_SCHEDULE_BADGES[event.scheduleMode] : undefined;
  const formatLabel = eventFormatLabel(event);
  const removeLabel = t('events:list.remove', { defaultValue: 'Remove event' });
  const copyLabel = t('events:list.copy', { defaultValue: 'Copy event' });

  return (
    <Table.Tr
      onClick={() => onOpen(event)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(event);
        }
      }}
      tabIndex={0}
      style={{ cursor: 'pointer', opacity: removing ? 0.5 : 1, transition: 'opacity 150ms ease' }}
    >
      <Table.Td style={{ width: COL.type }}>
        <ThemeIcon variant="light" color="brand" size={36} radius="md" style={{ fontSize: 18 }}>
          <span aria-hidden>{type?.emoji ?? '📅'}</span>
        </ThemeIcon>
      </Table.Td>
      <Table.Td>
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text size="md" fw={600} lineClamp={1}>
            {event.title || t('events:list.untitled', { defaultValue: 'Untitled event' })}
          </Text>
          {event.subtitle && (
            <Text size="md" c="dimmed" lineClamp={1}>
              {event.subtitle}
            </Text>
          )}
        </Stack>
      </Table.Td>
      <Table.Td style={{ width: COL.when }}>
        <Stack gap={4} align="flex-start">
          <Text size="md">{eventWhenText(event, t)}</Text>
          {scheduleBadge && (
            <Badge size="sm" variant="outline" color="grape">
              {scheduleBadge}
            </Badge>
          )}
        </Stack>
      </Table.Td>
      <Table.Td style={{ width: COL.join }}>
        <Stack gap={4} align="flex-start">
          <EventModeBadge eventMode={event.eventMode} />
          {formatLabel && (
            <Text size="md" c="dimmed">
              {formatLabel}
            </Text>
          )}
        </Stack>
      </Table.Td>
      <Table.Td style={{ width: COL.status }}>
        <Badge variant="light" color={status.color}>
          {status.label}
        </Badge>
      </Table.Td>
      <Table.Td style={{ width: COL.actions }}>
        <Group gap={4} wrap="nowrap" justify="flex-end">
          {onCopy && recordHasRight(event, 'duplicate') && (
            <Tooltip label={copyLabel} withArrow position="bottom">
              <ActionIcon
                variant="subtle"
                color="neutral"
                size="md"
                aria-label={copyLabel}
                // ⚠ The row itself opens the event: the button must not.
                onClick={(e) => {
                  e.stopPropagation();
                  onCopy(event);
                }}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <AppIcon iconName="Copy" size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {onRemove && eventRemovable(event) && (
            <Tooltip label={removeLabel} withArrow position="bottom">
              <ActionIcon
                variant="subtle"
                color="red"
                size="md"
                loading={removing}
                aria-label={removeLabel}
                // ⚠ The row itself opens the event: the button must not.
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(event);
                }}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <AppIcon iconName="Trash" size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          <AppIcon iconName="ChevronRight" size={18} />
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};

// A skeleton row that IS a real Table.Tr — the same columns, widths and element sizes as EventTableRow.
const EventTableRowSkeleton: React.FC = () => (
  <Table.Tr>
    <Table.Td style={{ width: COL.type }}>
      <Skeleton height={36} width={36} radius="md" />
    </Table.Td>
    <Table.Td>
      <Stack gap={8}>
        <Skeleton height={16} width="55%" maw={260} />
        <Skeleton height={14} width="35%" maw={180} />
      </Stack>
    </Table.Td>
    <Table.Td style={{ width: COL.when }}>
      <Skeleton height={16} width={170} />
    </Table.Td>
    <Table.Td style={{ width: COL.join }}>
      <Stack gap={6}>
        <Skeleton height={20} width={110} radius="xl" />
        <Skeleton height={14} width={70} />
      </Stack>
    </Table.Td>
    <Table.Td style={{ width: COL.status }}>
      <Skeleton height={20} width={84} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: COL.actions }}>
      <Group gap={4} wrap="nowrap" justify="flex-end">
        <Skeleton height={18} width={18} radius="sm" />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Phones + tablets: cards
// ============================================

interface CardProps extends RowProps {
  forceClose: boolean;
  onForceCloseComplete: () => void;
  onSwipeStateChange: (isOpen: boolean) => void;
}

const EventListCardComponent: React.FC<CardProps> = ({ event, onOpen, onRemove, onCopy, removing, forceClose, onForceCloseComplete, onSwipeStateChange }) => {
  const { t } = useTranslation(['events', 'common']);
  const canRemove = !!onRemove && eventRemovable(event);
  const canCopy = !!onCopy && recordHasRight(event, 'duplicate');

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (canCopy && onCopy) {
      actions.push({
        key: 'copy',
        icon: 'Copy',
        label: t('events:list.copyShort', { defaultValue: 'Copy' }),
        color: 'brand',
        onClick: () => onCopy(event),
        disabled: removing,
      });
    }

    if (canRemove && onRemove) {
      actions.push({
        key: 'remove',
        icon: 'Trash',
        label: t('common:buttons.remove', { defaultValue: 'Remove' }),
        color: 'red',
        onClick: () => onRemove(event),
        loading: removing,
        disabled: removing,
      });
    }

    return actions;
  }, [canCopy, onCopy, canRemove, onRemove, event, removing, t]);

  // Nothing to swipe to → the plain card (it still opens the event on tap).
  if (swipeActions.length === 0) {
    return <EventCard event={event} onOpen={onOpen} />;
  }

  return (
    <>
      {/* Phones: Copy and Remove are swipe actions (swipe left); the card itself carries no button. */}
      <Box hiddenFrom="sm">
        <SwipeableCard
          leftSwipeActions={swipeActions}
          forceClose={forceClose}
          onForceCloseComplete={onForceCloseComplete}
          onSwipeStateChange={onSwipeStateChange}
          disabled={removing}
          borderRadius="var(--mantine-radius-md)"
        >
          <EventCard event={event} onOpen={onOpen} removing={removing} />
        </SwipeableCard>
      </Box>
      {/* Tablets: the visible Copy and Remove buttons. */}
      <Box visibleFrom="sm">
        <EventCard event={event} onOpen={onOpen} onRemove={onRemove} onCopy={canCopy ? onCopy : undefined} removing={removing} />
      </Box>
    </>
  );
};

const EventListCard = React.memo(EventListCardComponent);

// Matches EventCard: the 44px type tile, title + status badge, subtitle, the when/format line, and the chevron.
// ⓘ No Remove circle — only an unpublished event has one, and on a phone it is a swipe, never a button.
const EventCardSkeleton: React.FC = () => (
  <Card withBorder radius="md" padding="md">
    <Group gap="sm" wrap="nowrap" align="center">
      <Skeleton height={44} width={44} radius="md" style={{ flexShrink: 0 }} />
      {/* EventCard's rows (#117): title + status, the sm subtitle, the xs when / where / format line — at its gap 2. */}
      <CardSkeletonLines count={3} sizes={['md', 'sm', 'sm']} gap={2} />
      <Skeleton height={18} width={18} radius="sm" style={{ flexShrink: 0 }} />
    </Group>
  </Card>
);

// ============================================
// Main component
// ============================================

const EventsListComponent: React.FC<EventsListProps> = ({ events, isLoaded, onOpen, onRemove, onCopy, isRemoving }) => {
  const { t } = useTranslation(['events', 'common']);
  const { isDesktop } = useAppEnvironment();
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  // Close any open swipe before acting, so the card is not left open behind the confirm.
  const handleRemove = useCallback(
    (event: Event) => {
      closeAllCards();
      onRemove?.(event);
    },
    [onRemove, closeAllCards],
  );

  const handleCopy = useCallback(
    (event: Event) => {
      closeAllCards();
      onCopy?.(event);
    },
    [onCopy, closeAllCards],
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard],
  );

  const showSkeleton = !isLoaded;
  const removeHandler = onRemove ? handleRemove : undefined;
  const copyHandler = onCopy ? handleCopy : undefined;

  if (isDesktop) {
    return (
      <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
        <Table.ScrollContainer minWidth={TABLE_MIN_WIDTH}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover={!showSkeleton}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: COL.type }} />
                <Table.Th>{t('events:list.columns.event', { defaultValue: 'Event' })}</Table.Th>
                <Table.Th style={{ width: COL.when }}>{t('events:list.columns.when', { defaultValue: 'When' })}</Table.Th>
                <Table.Th style={{ width: COL.join }}>{t('events:list.columns.join', { defaultValue: 'How guests join' })}</Table.Th>
                <Table.Th style={{ width: COL.status }}>{t('events:list.columns.status', { defaultValue: 'Status' })}</Table.Th>
                <Table.Th style={{ width: COL.actions }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: SKELETON_ROWS }).map((_, i) => <EventTableRowSkeleton key={i} />)
                : events.map((event) => (
                    <EventTableRow
                      key={event.id}
                      event={event}
                      onOpen={onOpen}
                      onRemove={removeHandler}
                      onCopy={copyHandler}
                      removing={!!event.id && isRemoving(event.id)}
                    />
                  ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    );
  }

  if (showSkeleton) {
    return (
      <Stack gap="sm" aria-busy="true">
        {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
          <EventCardSkeleton key={i} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      {events.map((event) => {
        const id = event.id || '';

        return (
          <EventListCard
            key={id}
            event={event}
            onOpen={onOpen}
            onRemove={removeHandler}
            onCopy={copyHandler}
            removing={!!id && isRemoving(id)}
            forceClose={shouldForceClose(id)}
            onForceCloseComplete={() => handleForceCloseComplete(id)}
            onSwipeStateChange={(isOpen) => handleSwipeStateChange(id, isOpen)}
          />
        );
      })}
    </Stack>
  );
};

export const EventsList = React.memo(EventsListComponent);
